import { AdNode } from "./AdNode";
import type {
  BaseSlotName,
  SlotConfig,
  SlotConfigMap,
  SlotName,
  VideoSlotName,
} from "../config/platforms/types";
import type { GptWrapper } from "./GptWrapper";
import invariant from "tiny-invariant";
import { registerAdNodeComponent } from "./AdNodeComponent";
import { getBaseSlotName } from "../helpers/repeatableSlotNames";
import { DEFAULT_NODE_NEAR_VIEWPORT_MARGIN } from "./consts";
import type { AdEngine } from "./AdEngine";

export class AdNodeManager {
  private adNodes: AdNode[] = [];
  private observer: IntersectionObserver | undefined;

  constructor(
    private readonly slotConfigMap: SlotConfigMap,
    private readonly gptWrapper: GptWrapper,
  ) {
    this.registerAdNodeComponent();
  }

  /**
   * Get the SlotConfigMap created during init of the ad engine. This includes any changes that
   * occurred during the `initSlotConfigMap` hook.
   *
   * @returns The full slotConfigMap object created during initialization of the ad engine
   */
  getSlotConfigMap(): SlotConfigMap {
    return this.slotConfigMap;
  }

  /**
   * Returns the SlotConfig object for the provided slot name. Any form of the slot name can be
   * provided, including video slots and repeatable slot names.
   *
   * @param slotName The slot name to get the configuration for. This can be any version of the slot
   * name including base slot, slot id, video slot name, or repeatable slot name with suffix.
   * @returns The slot config for the display or video slot. Returns undefined if the config cannot
   * be found for that slot name.
   */
  getSlotConfig(slotName: SlotName): SlotConfig | undefined {
    return (
      this.slotConfigMap.baseSlots[slotName as BaseSlotName] ??
      this.slotConfigMap.baseSlots[getBaseSlotName(slotName)] ??
      this.slotConfigMap.videoSlots[slotName as VideoSlotName]
    );
  }

  /**
   * Creates a new AdNode and adds it to the adNodes array which is checked before requesting bids
   * and later GAM. This will also call `gptWrapper.defineSlot()` to define the slot in googletag,
   * so the dom element for the node must exist in the dom _before_ calling this function.
   *
   * @param slotName The id of the dom element being created.
   * @param slotConfigOverride Optional slot config. By default the configuration will be retrieved from the
   * slot config passed into the AdNodeManager on creation of the manager.
   * @returns The created adNode or undefined if the adNode could not be created due to the slot
   * config being undefined.
   */
  createAdNode(
    slotName: SlotName,
    slotConfigOverride?: SlotConfig,
  ): AdNode | undefined {
    const slotConfig = slotConfigOverride ?? this.getSlotConfig(slotName);
    if (!slotConfig) {
      console.warn(
        `Cannot create adNode, no slot config found for slotName=[${slotName}]`,
      );
      return undefined;
    }
    const adNode = new AdNode(slotName, slotConfig, this.gptWrapper);
    this.adNodes.push(adNode);

    this.gptWrapper.defineSlot(adNode);

    // Register the adNode with the intersection observer if the observer is already initialized
    // If the observer is not initialized yet, then the node will be registered when its created
    const element = adNode.getElement();
    if (this.observer && element) {
      this.startObservingElement(element);
    }

    return adNode;
  }

  /**
   * Removes the adNode from the adNodes array, so it won't be requested nor bid for
   * and destroys the GPT slot related to the adNode. If the adNode has an element on
   * the page it will also stop observing it with the IntersectionObserver.
   *
   * @param adNodeToRemove ad node to remove
   */
  removeAdNode(adNodeToRemove: AdNode) {
    const element = adNodeToRemove.getElement();
    if (element) {
      this.stopObservingElement(element);
    }
    adNodeToRemove.destroy();
    this.adNodes = this.adNodes.filter(
      (adNode) => adNode.getSlotName() !== adNodeToRemove.getSlotName(),
    );
  }

  getAdNodes(): AdNode[] {
    return this.adNodes;
  }

  getAdNodeByName(slotName: SlotName): AdNode | undefined {
    let adNode = this.adNodes.find(
      (adNode) => adNode.getSlotName() === slotName,
    );
    /* This is a special case check for out of page slots defined with an OutOfPageFormat.
     * Since these slots don't follow the same naming convention as regular slots,
     * we also need to check if the adNode has a base slot name that matches the provided slotName.
     * The slotName gets adjusted in the GptWrapper.defineOutOfPageSlot() function,
     * through the AdNode.setOutOfPageSlotName() function.
     * */
    adNode ??= this.adNodes.find(
      (adNode) => adNode.getBaseSlotName() === slotName,
    );
    return adNode;
  }

  private getAdNodeByNameOrFail(slotName: SlotName) {
    const adNode = this.getAdNodeByName(slotName);

    invariant(adNode, `adNode is not defined: ${slotName}`);

    return adNode;
  }

  blockSlot(slotName: SlotName): void {
    this.getAdNodeByNameOrFail(slotName).block();
  }

  unblockSlot(slotName: SlotName): void {
    this.getAdNodeByNameOrFail(slotName).unblock();
  }

  /**
   * Registers the `<fandom-ad />` web component with the dom. This attaches the
   * AdNodeComponent class to the `<fandom-ad />` element, allowing any current
   * and future `<fandom-ad />` elements to be automatically registered as ad
   * nodes with the AdNodeManager.
   */
  registerAdNodeComponent() {
    // support `<fandom-ad />` web component
    registerAdNodeComponent(this);
  }

  /**
   * This method establishes the IntersectionObservers that are necessary
   * to make sure that each adNode is requested as soon as it gets close
   * enough to the viewport. It also makes the first call to gptWrapper.requestSlots
   * for all the initial adNodes.
   *
   * - Adds an IntersectionObserver
   * - Begins observing every existing adNode
   * - The first time an adNode gets close enough the viewport, request the slots from the gptWrapper
   */
  requestFirstCallSlotsAndObserveIntersection(adEngine: AdEngine) {
    this.startObservingSlots((entries: IntersectionObserverEntry[]) => {
      // slots that should be called in first ad call even if not visible

      const firstCallSlots = this.getAdNodes().filter(
        (adNode) =>
          adNode.getSlotConfig().isFirstCall && !adNode.hasEverBeenRequested(),
      );

      // slots being called for the first time (e.g. if they are just coming into the viewport)
      const newSlots = entries
        .map((entry) => {
          if (entry.intersectionRatio > 0) {
            const adNode = this.getAdNodeByName(entry.target.id as SlotName);
            if (adNode && !adNode.hasEverBeenRequested()) {
              return adNode;
            }
          }
        })
        .filter((adNode) => adNode !== undefined);

      adEngine.requestRender([...new Set([...newSlots, ...firstCallSlots])]);
    });
  }

  private startObservingSlots(
    intersectionObserverCallback: IntersectionObserverCallback,
  ) {
    this.observer = new IntersectionObserver(intersectionObserverCallback, {
      root: null,
      rootMargin: `${DEFAULT_NODE_NEAR_VIEWPORT_MARGIN}px 0px ${DEFAULT_NODE_NEAR_VIEWPORT_MARGIN}px 0px`,
      threshold: 1,
    });

    this.getAdNodes().forEach((adNode) => {
      const adNodeElement = adNode.getElement();
      if (adNodeElement) {
        this.startObservingElement(adNodeElement);
      }
    });
  }

  startObservingElement(adNodeElement: HTMLElement) {
    invariant(this.observer, "IntersectionObserver is not initialized");
    this.observer.observe(adNodeElement);
  }

  stopObservingElement(adNodeElement: HTMLElement) {
    invariant(this.observer, "IntersectionObserver is not initialized");
    this.observer.unobserve(adNodeElement);
  }

  addAllStaticSlots() {
    const slotConfigMap = this.getSlotConfigMap().baseSlots;

    // Add static slots
    Object.values(slotConfigMap).forEach((slotConfig) => {
      if (slotConfig && !slotConfig.dynamic && !slotConfig.webComponent) {
        // Only render slots that are present on the page
        const el = document.getElementById(slotConfig.name);
        if (el) {
          this.createAdNode(slotConfig.name, slotConfig);
        }
      }
    });
  }
}
